import { Link, useParams } from '@tanstack/react-router';
import type { ComponentProps, FC } from 'react';

import {
  buildLocaleParams,
  ensureLocalePrefixedPath,
  resolveAppLocale,
} from '~/utils/locale-path';

type BaseLinkProps = ComponentProps<typeof Link>;

interface LocalizedLinkProps extends Omit<BaseLinkProps, 'to' | 'params'> {
  to: string;
  params?: Record<string, unknown>;
  localeOverride?: string;
}

export const LocalizedLink: FC<LocalizedLinkProps> = ({
  to,
  params,
  localeOverride,
  ...rest
}) => {
  // Get current locale from route params, fallback to default
  const routeParams = useParams({ strict: false });
  const rawLocale = (routeParams as Record<string, string>)?.locale;

  // Validate locale - if invalid or not provided, use default
  const currentLocale = resolveAppLocale(localeOverride ?? rawLocale);

  const target = ensureLocalePrefixedPath(to, currentLocale) as BaseLinkProps['to'];

  const mergedParams = buildLocaleParams(currentLocale, params);

  return <Link {...rest} params={mergedParams as unknown as BaseLinkProps['params']} to={target} />;
};
